import { createRouter, createWebHistory } from 'vue-router'
import store from '../store/index'
import ability from '@u/ability'
import { helper } from '@u/helper'
import i18n from '@/i18n'
import { canAccess } from './access'

import MainLayout from '../components/layouts/main-layout/MainLayout'
import TheNotFoundPage from '@p/TheNotFoundPage.vue'

import { workRootPages, workSubPages } from './works'
import { urnRootPages } from '@/router/urn'
import { loginRootPages } from './login/index'
import { planningRootPages, planningSubPages } from './planning'
import { invoicingRootPages, invoicingSubPages } from './invoicing'
import { noteRootPages } from '@/router/note'
import { statsRootPages } from '@/router/stats'
import { settingsSubPages } from './settings/index'
import { profileRootPages } from './profile/index'

const routes = [
  { ...loginRootPages },
  {
    path: '/',
    name: 'root',
    component: MainLayout,
    beforeEnter() {
      return store.getters['auth/isAuthenticated'] ? true : { name: 'login' }
    },
    children: [
      { ...workRootPages },
      { ...urnRootPages },
      { ...planningRootPages },
      { ...invoicingRootPages },
      { ...noteRootPages },
      { ...statsRootPages },
      { ...profileRootPages },
      ...settingsSubPages,
      ...workSubPages,
      ...invoicingSubPages,
      ...planningSubPages,
      // Any URL no route above matches.
      {
        path: ':pathMatch(.*)*',
        name: 'not_found',
        component: TheNotFoundPage
      }
    ]
  }
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    return savedPosition || { left: 0, top: 0 }
  }
})

// The user (permissions, settings) is fetched before the first page of the
// session, then each page is checked against the access rules: a customer or
// a user without the permission lands on the works list instead of an empty
// page the API refuses.
router.beforeEach(async (to) => {
  if (to.name === 'login' || !store.getters['auth/isAuthenticated']) {
    return true
  }
  if (!store.getters['auth/getAuth']?.id) {
    try {
      await store.dispatch('auth/refreshAuth')
    } catch {
      // A 401 sends to the login page (apiClient). Any other failure (5xx,
      // network) has been toasted: the page opens all the same and
      // MainLayout fetches the user again, the API still checking access.
      return true
    }
  }
  const allowed = canAccess(to, {
    can: (permission) => ability.can(permission, 'all'),
    isCustomer: store.getters['auth/getAuthRole'] === 'customer-user'
  })
  if (allowed) return true
  helper.toastWarning(i18n.global.t('toasts.page_not_allowed'))
  return to.name === 'work_progress_route'
    ? false
    : { name: 'work_progress_route' }
})

export default router
